// Media queries used by the shell and the parts (plan 8.3: phone ≤ 760, tablet ≤ 1099).
import { useSyncExternalStore } from 'react';

export const MQ = {
  mobile: '(max-width: 760px)',
  tablet: '(max-width: 1099px)',
  reduceMotion: '(prefers-reduced-motion: reduce)',
  dark: '(prefers-color-scheme: dark)',
  hover: '(hover: hover) and (pointer: fine)',
} as const;

function mql(query: string): MediaQueryList | null {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(query) : null;
}

/** Current value without subscribing (event handlers, imperative code). Hover defaults to true like the mockup. */
export function matches(query: string): boolean {
  const m = mql(query);
  return m ? m.matches : query === MQ.hover;
}
export const isMobile = (): boolean => matches(MQ.mobile);
export const isTablet = (): boolean => matches(MQ.tablet) && !matches(MQ.mobile);
export const reduceMotion = (): boolean => matches(MQ.reduceMotion);
export const prefersDark = (): boolean => matches(MQ.dark);
export const canHover = (): boolean => matches(MQ.hover);

/** React hook: re-renders when the media query result changes. */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (onChange) => {
      const m = mql(query);
      if (!m) return () => {};
      m.addEventListener?.('change', onChange);
      return () => m.removeEventListener?.('change', onChange);
    },
    () => matches(query),
    () => false,
  );
}
export const useIsMobile = (): boolean => useMediaQuery(MQ.mobile);
export const useIsTablet = (): boolean => useMediaQuery(MQ.tablet);
export const useReducedMotion = (): boolean => useMediaQuery(MQ.reduceMotion);
export const usePrefersDark = (): boolean => useMediaQuery(MQ.dark);
